(ns dashboard.views.organization.view
  (:require ["react" :as react]
            [dashboard.components.breadcrumbs :refer [breadcrumbs]]
            [dashboard.entity.analysis.db :as analysis.db]
            [dashboard.entity.analysis.modals :as asa.view]
            [dashboard.entity.organization.db :as org.db]
            [dashboard.entity.organization.profile :as components]
            [dashboard.subs :as dashboard.subs]
            [dashboard.ui :as ui]
            [io.repliance.schemas.organization :as org]
            [re-frame.core :as rf]
            [reitit.frontend.easy :as rfe]))

(defn thumbnail-card
  "Displays a thumbnail representation of a person within an organization."
  [{:keys [person-id fullname title]}]
  [:div.tf-nc.thumbnail-card
   [:a {:href (rfe/href org.db/view-id-member
                        {:selected-person-id person-id})}
    fullname]
   [:div title]
   [:a.icon-new-child
    {:href (rfe/href org.db/view-id-member-action
                     {:selected-person-id person-id
                      :action "new"})}
    [:i.fas.fa-circle-plus]]])

(defn revision-history
  "Displays a revision history for the person in a modal.
   Accessed via the /history route."
  [person-id organization-members]
  (let [history @(org.db/person-revision-history* person-id)
        names-map (->> organization-members
                       (mapv
                        (fn [{:keys [fullname person-id]}]
                          {person-id fullname}))
                       (apply merge))]
   [components/revision-history-modal
    {:data history
     :person-names names-map
     :member-view org.db/view-id-member
     :perform-navigation (fn [& args] (apply org.db/perform-navigation args))
     :person-id person-id}]))

(defn person-responsibilities
  "Displays a revision history for the person in a modal.
   Accessed via the /history route."
  [{:keys [person-id] :as person} {:keys [on-close ctx]}]
  (let [responsibilities @(org.db/person-asa-responsibilities*
                           person-id)
        asas @(analysis.db/structured-analyses-with-applied-joined*)]
    [:<>
     [:f> components/responsibilities-modal
     {:data responsibilities
      :analyses asas
      :person person
      :on-open-modal (fn [analysis-id mode applied-structured-analysis]
                       (prn "I get" analysis-id mode applied-structured-analysis)
                       (asa.view/show-editor-modal
                        mode
                        analysis-id
                        applied-structured-analysis))
      :on-close on-close
      :close-href (:close-href ctx)}]]))

(defn draw-node
  "Recursively renders each node in the org tree.
   Look out! Named recursion!"
  [{:keys [selected-person-id action]
    :as selection}
   {:keys [person-id reports] :as member}
   members]
  (let [form-errors @(org.db/form-errors*)
        [active-ref set-active-ref] (react/useState nil)
        on-submit (fn [base-map e]
                    (.preventDefault e)
                    (org.db/save-person
                     (cond-> base-map
                       (not person-id)
                       (assoc :create-new true))
                     identity
                     nil))
        content (fn [{:keys [person-id]}]
                  [:div.level.organization-person-actions
                   [:div.buttons.level-right
                    [:a.button.is-rounded.is-info.organization-person-profile-button
                     {:href (rfe/href org.db/view-id-member-action {:selected-person-id person-id
                                                                :action "history"})
                      :on-click org.db/fetch-person-revision-history}
                     "Revision History"
                     [:span.icon>i.fas.fa-timeline]]
                    [:a.button.is-rounded.is-primary.organization-person-profile-button
                     {:href (rfe/href org.db/view-id-member-action {:selected-person-id person-id
                                                                :action "responsibilities"})
                      :on-click org.db/fetch-person-asa-responsibilities}
                     "Responsibilities"
                     [:span.icon>i.fas.fa-magnifying-glass-chart]]]])]
   ;; When the active person changes, we scroll to it
   (react/useEffect (fn []
                      (when active-ref
                        (.scrollIntoView active-ref)))
    (array active-ref))

   [:li.organization-node
    (if (and (= selected-person-id person-id)
             (not (= "new" action)))
      [:f> components/member-profile
       member
       on-submit
       members
       form-errors
       content
       set-active-ref
       org.db/view-id-organization]
      [thumbnail-card member])
    (when reports
      (-> reports
          (->> (sort-by :person-id)
               (map (fn [person]
                      [:f> draw-node selection person members]))
               (into [:ul]))
          (conj (when (and (= selected-person-id person-id)
                           (= "new" action))
                  [:li
                   [:f> components/member-profile
                    {:reports-to-person-id person-id}
                    on-submit
                    members
                    form-errors
                    nil
                    set-active-ref
                    org.db/view-id-organization]]))))]))

(defn organization-profile
  "Given the router props stored in the app DB from ::db/active-state, and a
   vector of maps representing persons, arranges the org chart into a tree
   structure and then recursively draws the tree"
  [props organization-members]
  [:div.tf-tree
   (->> organization-members
        org/build-tree
        (mapv (fn [person]
                [:f> draw-node props person organization-members]))
        (into [:ul]))])

(defn view
  "Main view of the organization and its employees (persons) within it."
  []
  (let [props (-> @(rf/subscribe [::dashboard.subs/route-parameters])
                  :path)
        organization-members @(org.db/organization-members*)
        selected-person-id (:selected-person-id props)
        responsibilities (when (and (>= (:selected-person-id props) 0)
                                    (= (:action props) "responsibilities"))
                           @(org.db/person-asa-responsibilities* selected-person-id))
        member (->> organization-members
                    (filter (fn [{:keys [person-id]}]
                              (= person-id (:selected-person-id props))))
                    first)
        asas @(analysis.db/structured-analyses-with-applied-joined*)
        responsibility-modal-open @(org.db/get-responsibility-modal-open*)]
    (react/useEffect
     (fn []
       (when (and (not responsibility-modal-open)
                  (>= (:selected-person-id props) 0)
                  (= (:action props) "responsibilities")
                  (seq organization-members)
                  (seq asas))
         (org.db/set-responsibility-modal-open true)
         (rf/dispatch [::ui/push-modal
                       {:on-close (fn []
                                    (rf/dispatch [::ui/pop-modal]))
                        :ctx {:close-href (rfe/href org.db/view-id-member {:selected-person-id (:selected-person-id props)})}
                        :content-props {:class "organization-responsibility-modal"}
                        :component (partial person-responsibilities
                                            member)}]))
       (fn [] (when-not (= (:action props) "responsibilities")
                (org.db/set-responsibility-modal-open false)
                (rf/dispatch [::ui/pop-modal]))))
     (array props responsibility-modal-open))
    [:div
     ;;{:key (keyword selected-person-id responsibility-modal-open)}
     [breadcrumbs {:items (org.db/breadcrumbs)}]
     [:div.section
      [:p.title.is-3 "Organization"]
      (when (= "history" (:action props))
        [:f> revision-history (:selected-person-id props) organization-members])
      (when (= 1 (count organization-members))
        [:div.notification
         [:div.title.is-4 "Welcome to the Organization Chart"]
         [:div "The purpose of this module is to allow Analysts to document
                individuals within the client organization, and capture the
                hierarchical relationships and reporting structures."]
         [:br]
         [:div "To begin, press the "
          [:i.fas.fa-circle-plus]
          " button under the organization root to add the first member of the
           organization."]
         [:br]
         [:div "Alternatively, you can click here to "
          [:a.icon-new-child
           {:href (rfe/href org.db/view-id-member-action
                            {:selected-person-id 0
                             :action "new"})}
           "add your first organization member"]
          "."]])
      [:f> organization-profile props organization-members responsibilities]]]))
